import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { Column } from '@/components/SettingsLayout';
import { Text } from '@/ui';
import { useGetApiKeysQuery } from '@/store/slices/orgApiKeySlice';
import { debounce } from '@/utils/helpers/debounce';
import SkeletonTable from '@/components/SkeletonLoader/SkeletonTable';
import ApiKeysFilters from './ApiKeysFilters';
import ApiKeysTable from './ApiKeysTable';
import AddApiKeyModal from './AddApiKeyModal';
import DeleteApiKeyModal from './DeleteApiKeyModal';

function OrgApiKeys() {
  const { t } = useTranslation();
  const { id: organizationId } = useParams();
  const [search, setSearch] = useState('');

  const { data: apiKeys, isLoading } = useGetApiKeysQuery(
    organizationId ? { organizationId, search: search || undefined } : skipToken,
  );

  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setSearch(value);
    }, 300),
    [],
  );

  const handleFiltersChange = useCallback(
    (filters: { search?: string }) => {
      debouncedSetSearch(filters.search || '');
    },
    [debouncedSetSearch],
  );

  if (isLoading) {
    return (
      <Column gapSize='24'>
        <Text size='l' weight='semi-bold'>
          {t('ApiKeys.title')}
        </Text>
        <SkeletonTable rows={3} />
      </Column>
    );
  }

  const hasApiKeys = apiKeys && apiKeys.length > 0;

  return (
    <Column gapSize='24'>
      <Text size='l' weight='semi-bold'>
        {t('ApiKeys.title')}
      </Text>
      <ApiKeysFilters onFiltersChange={handleFiltersChange} />
      {hasApiKeys ? (
        <ApiKeysTable apiKeys={apiKeys} />
      ) : (
        <div className='w-full flex items-center justify-center py-12 border rounded-md'>
          <Text size='s' color='base-grey'>
            {t('ApiKeys.empty_state')}
          </Text>
        </div>
      )}
      <AddApiKeyModal />
      <DeleteApiKeyModal />
    </Column>
  );
}

export default OrgApiKeys;
